import React from 'react';
import { registerWidget } from '../widgetRegistry';
import { useLayoutCtx } from '../LayoutContext';

// L1 strategy: every BUILTIN_* tree is a single-widget tree pointing at one
// of these wrapper widgets. The widgets are pure pass-throughs — they expect
// the host (CaseClient.jsx) to supply a `renderBuiltin(ctx)` callback that
// returns the actual UI for the surface. Keeping the widgets free of any
// reference to today's components avoids circular imports between
// CaseClient.jsx → LayoutRenderer.jsx → widgets → CaseClient.jsx.
//
// In L3, an admin authoring a custom layout from scratch will see these in
// the palette as "default" widgets — drag in `case.builtin` to get the
// vanilla case-detail surface, then add finer-grained widgets around it.

const FALLBACK = (
  <div style={{ padding: '16px', color: 'var(--text-secondary)', fontSize: '13px' }}>
    Built-in renderer not provided. (Mount this widget through &lt;LayoutRenderer&gt; with
    a <code>callbacks.renderBuiltin</code> function.)
  </div>
);

function makeBuiltinWidget(label) {
  function BuiltinWidget() {
    const ctx = useLayoutCtx();
    const fn = ctx.callbacks && ctx.callbacks.renderBuiltin;
    if (typeof fn !== 'function') return FALLBACK;
    return fn(ctx) || null;
  }
  BuiltinWidget.displayName = label;
  return BuiltinWidget;
}

registerWidget({
  type: 'home.builtin',
  categories: ['home'],
  palette_label: 'Cases home (default)',
  description: 'The standard Case Client home surface — cases dashboard + grid + search bar.',
  Component: makeBuiltinWidget('HomeBuiltin'),
  configSchema: null,
  publishes: ['xv.OpenCase', 'xv.AddCase', 'xv.SearchCases'],
  subscribes: ['xv.RefreshContext'],
  group: 'Home',
});

registerWidget({
  type: 'case.builtin',
  categories: ['case'],
  palette_label: 'Case detail (default)',
  description: 'The standard Case Client case-detail surface — header, tabs, edit form, status changer, work-item launcher.',
  Component: makeBuiltinWidget('CaseBuiltin'),
  configSchema: null,
  publishes: ['xv.CaseSaved', 'xv.CaseStatusChanged', 'xv.OpenWorkItem', 'xv.CommentAdded', 'xv.TaskLaunched'],
  subscribes: ['xv.RefreshContext'],
  group: 'Case',
});

registerWidget({
  type: 'task.builtin',
  categories: ['task'],
  palette_label: 'Work item (default)',
  description: 'The standard worker-step surface — case summary header, work-item form, Save Draft / Complete Step toolbar.',
  Component: makeBuiltinWidget('TaskBuiltin'),
  configSchema: null,
  publishes: ['xv.WorkItemSaved', 'xv.WorkItemCompleted', 'xv.WorkItemReleased'],
  subscribes: ['xv.RefreshContext'],
  group: 'Task',
});

registerWidget({
  type: 'add_case.builtin',
  categories: ['add_case'],
  palette_label: 'Add case (default)',
  description: 'The standard Add Case form — title / description / priority + intake form, Submit / Cancel.',
  Component: makeBuiltinWidget('AddCaseBuiltin'),
  configSchema: null,
  publishes: ['xv.AddCaseSubmitted', 'xv.AddCaseCancelled', 'xv.CaseCreated'],
  subscribes: [],
  group: 'Add Case',
});

registerWidget({
  type: 'add_task.builtin',
  categories: ['add_task'],
  palette_label: 'Add task (default)',
  description: 'The standard Launch Task form — case summary + launch form, Launch / Cancel.',
  Component: makeBuiltinWidget('AddTaskBuiltin'),
  configSchema: null,
  publishes: ['xv.AddTaskSubmitted', 'xv.AddTaskCancelled', 'xv.TaskLaunched'],
  subscribes: [],
  group: 'Add Task',
});
